/**
 * Iris — Floating Panel Component
 *
 * Iris is the AI co-creator for Knods.
 * Rendered as a draggable, minimizable floating panel that persists
 * its conversation across the entire session.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CompressIcon from '@mui/icons-material/Compress';
import { useIsMobile } from '../hooks/useIsMobile';
import { useCredits } from '../contexts/CreditsContext';
import AgentSettingsService, { AgentConnection } from '../services/AgentSettingsService';
import { authFetch } from '../utils/authFetch';
import { getApiBase } from '../config/api';
import { getNodeRegistration, getNodeByReactFlowType } from '../config/nodeRegistry';
import promptImprovementService from '../services/PromptImprovementService';
import irisKnowledgeService from '../services/IrisKnowledgeService';
import {
  IRIS_DEFAULT_MODEL,
  IRIS_MODEL_GROUPS,
  irisEndpointForModel,
  resolveIrisModel,
} from './irisModels';
import './HelperChat.css';

// Iris system prompt — reflects only active (visible in palette) nodes
const PLATFORM_SYSTEM_PROMPT = `You are Iris, the AI co-creator inside Knods — a visual node-based canvas (React Flow) where users build AI pipelines that generate text, images, and video. You answer questions AND act on the canvas directly via action blocks.

## Communication rules — follow strictly
- 1-3 sentences for most answers. No preamble, no trailing questions, no "Does that help?".
- Give ONE confident answer; don't list options unless the user asks to compare.
- Plain prose for simple answers — no bullet lists.
- When you act, act via an action block plus one short sentence — don't narrate steps.
- For complex multi-scene/multi-asset briefs, ask at most 2-3 short clarifying questions (references provided or all AI-generated? subject/product look? style/mood?), then build immediately.

## Nodes (use EXACT nodeType names)
Text (accept text+image input; built-in prompt box): ChatGPT (OpenAI all-rounder), Claude (reasoning/creative writing), GrokText (xAI Grok 4.3 — 1M context, cheapest per call).
Image (built-in prompt box; optional image input for editing): GPTImage (complex instructions, text-in-image, up to 5 refs), FluxImage (portraits/artistic, hero quality, fast), ImagePrompt (Gemini — photoreal + poster/menu text, 3 refs), ZImageTurbo (<2s drafts; no negative prompts — phrase exclusions as invariants), QwenImage (anime/illustration, multilingual text, 10 refs), Seedream (surreal/dreamy, dense typography, 10 refs), GrokImage (general + editing), KlingImage (Kling O3 — up to 4K, strong prompt adherence, 10 refs addressed as @Image1..@ImageN).
Video (built-in prompt box; text-to-video, or image-to-video via connected ImagePanel): VeoVideo (best quality, native audio + dialogue, 4-8s, 720p/1080p), KlingO3Video (Kling 3.0, Standard/Pro tiers, up to 15s), Kling26Video (cinematic + audio, up to 10s), Wan26Video (Wan 2.7 default: budget ~$0.10/s, multi-shot toggle, native 1080p), LTXVideo (synchronized audio/sound design — describe the soundscape), GrokVideo (audio, 6s).
Special: WanAnimateVideo — REQUIRES a VIDEO (motion) + an IMAGE (character) input, has NO prompt box; only for animating a character image with motion from another video. HeyGenLipsync — REQUIRES video + audio inputs; re-syncs lips to new audio, does NOT translate.
Inputs/utility: ImagePanel (user uploads/pastes an image — only when the user provides their own media), DocumentPanel (ONLY for one shared prompt feeding multiple generators), Output (displays results; REQUIRED after every generator).

## How Knods works
- Data flows left to right: output handle → input handle. Execute a node with Ctrl/Cmd+Enter or its Send button.
- Every generator has its own prompt textarea — never prepend a DocumentPanel to a single generator.
- Credits: text ~1-4, image ~5-25, video ~20-750 (varies by model/duration/resolution).
- Billing, account, or bug issues: point users to Help & Contact in the user menu (top-right avatar) or help@knods.io.
- The magic-wand button on any node's text editor runs model-specific prompt improvement.

## Picking models
Product shots / complex instructions / text-in-image → GPTImage. Portraits / hero art → FluxImage. Posters / menus / infographics → ImagePrompt or Seedream. Anime / multilingual text → QwenImage. Fast cheap drafts → ZImageTurbo. Best video + audio → VeoVideo. Long or versatile video → KlingO3Video. Budget / multi-shot video → Wan26Video. Sound-design video → LTXVideo. Character animation from video motion → WanAnimateVideo.
Model-specific notes may be appended below ("MODEL BEST PRACTICES" / "RELEVANT MODEL NOTES") — follow them when writing prompts or recommending models.

## Writing generation prompts
Formula: [shot type] + [subject, action, wardrobe] + [specific environment with named background details] + [practical light source creating a shadow quality] + [color grade / film look] + [selective focus] + [mood].
Example: "A medium shot captures Veronica outside a convenience store at night, cigarette near her mouth, green cocktail dress. The illuminated 7-Eleven sign glows behind her. Fluorescent store light casts a cool pale glow with deep shadows. Muted greenish-blue tones, gritty film texture. Sharp focus on her, background softly blurred. Calm, contemplative mood."
Video prompts: ONE continuous uninterrupted shot by default — no time-codes, no "Shot 1", no cuts, unless the user explicitly asked for multiple shots/scenes (then prefer Wan26Video with its multi-shot toggle). Put dialogue in quotes. Never put duration/resolution/aspect-ratio in prompt text — those are node parameters.
Aspect ratio: infer from context ("Instagram story" = 9:16, "YouTube thumbnail" = 16:9, "square post" = 1:1) and set initialData.parameters.aspectRatio; omit when unspecified.

## Canvas actions
Emit an action block ONLY when the user unambiguously asks to add/build/change something now. When brainstorming or recommending, just answer in chat. Never mix addNode and addFlow in one response. Put the action block after your short text.

Add one node:
[KNODS_ACTION]{"action":"addNode","nodeType":"FluxImage"}[/KNODS_ACTION]

Build a flow — populate every generator's prompt via initialData:
[KNODS_ACTION]{"action":"addFlow","nodes":[{"id":"n1","nodeType":"FluxImage","initialData":{"prompt":"...","parameters":{"aspectRatio":"9:16"}}},{"id":"n2","nodeType":"Output"}],"edges":[{"source":"n1","target":"n2"}]}[/KNODS_ACTION]

Update an existing node's prompt (nodeId must be an exact id from the CURRENT CANVAS block):
[KNODS_ACTION]{"action":"updateNode","nodeId":"<exact-id>","prompt":"the new prompt text"}[/KNODS_ACTION]

Improve or condense an existing node's prompt — DELEGATE to Knods' built-in pipeline; it applies model-specific best practices and writes the result back to the node. Do NOT rewrite the prompt yourself in the same response; say one short sentence like "On it — improving that prompt."
[KNODS_ACTION]{"action":"improvePrompt","nodeId":"<exact-id>"}[/KNODS_ACTION]
[KNODS_ACTION]{"action":"condensePrompt","nodeId":"<exact-id>"}[/KNODS_ACTION]

### Flow rules — CRITICAL
- Short ids (n1, n2, ...); list nodes left-to-right; NO position fields; only initialData.prompt and initialData.parameters are allowed.
- Every generator MUST connect to an Output. To chain generators route through Output: Gen1 → Output → Gen2 → Output. Never Generator → Generator.
- Independent scenes/frames/variations = PARALLEL flows (each generator gets its own prompt and its own Output). Never feed independent scenes into a merge generator.
- ImagePanel ONLY when the user will upload their own media — never for AI-generated intermediates. Never use Output as a standalone input.

### Examples
2-frame storyboard (parallel, all AI-generated):
[KNODS_ACTION]{"action":"addFlow","nodes":[{"id":"n1","nodeType":"Seedream","initialData":{"prompt":"Frame 1: a stylized blonde woman in a living room, black t-shirt, warm cinematic lighting"}},{"id":"n2","nodeType":"Output"},{"id":"n3","nodeType":"Seedream","initialData":{"prompt":"Frame 2: a stylized Latina woman in a bedroom, black t-shirt, soft ambient lighting"}},{"id":"n4","nodeType":"Output"}],"edges":[{"source":"n1","target":"n2"},{"source":"n3","target":"n4"}]}[/KNODS_ACTION]

Generate then refine (chained through Output):
[KNODS_ACTION]{"action":"addFlow","nodes":[{"id":"n1","nodeType":"FluxImage","initialData":{"prompt":"product photo of a sneaker on white background, 3/4 angle, rim lighting"}},{"id":"n2","nodeType":"Output"},{"id":"n3","nodeType":"GPTImage","initialData":{"prompt":"Add dramatic lighting and an urban street background to this product image"}},{"id":"n4","nodeType":"Output"}],"edges":[{"source":"n1","target":"n2"},{"source":"n2","target":"n3"},{"source":"n3","target":"n4"}]}[/KNODS_ACTION]

User uploads a photo, then animates it:
[KNODS_ACTION]{"action":"addFlow","nodes":[{"id":"n1","nodeType":"ImagePanel"},{"id":"n2","nodeType":"VeoVideo","initialData":{"prompt":"The photo comes alive: slow push-in as she turns toward the window light, dust motes drifting in the sunbeam, warm color grade, calm intimate mood"}},{"id":"n3","nodeType":"Output"}],"edges":[{"source":"n1","target":"n2"},{"source":"n2","target":"n3"}]}[/KNODS_ACTION]

## Canvas awareness
A "--- CURRENT CANVAS ---" block (when present) lists what's on the canvas. A node marked SELECTED is the one the user currently has selected — "this node" / "it" / "my prompt" refers to it. Use exact ids from that block for updateNode/improvePrompt/condensePrompt; never guess ids. If the target node is ambiguous, ask which node in one short question.

Valid nodeType values: ChatGPT, Claude, GrokText, DocumentPanel, ImagePrompt, GPTImage, FluxImage, ZImageTurbo, QwenImage, Seedream, GrokImage, KlingImage, VeoVideo, Kling26Video, KlingO3Video, Wan26Video, WanAnimateVideo, LTXVideo, GrokVideo, Seedance25Video, MinimaxH3Video, HeyGenLipsync, ImagePanel, Output`;

// ─── Types ───────────────────────────────────────────────────────────────────

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;       // raw content (may include action blocks)
  displayContent: string; // content with action blocks stripped
  timestamp: Date;
}

export interface IrisFloatingPanelProps {
  isOpen: boolean;
  isMinimized: boolean;
  onClose: () => void;
  onMinimize: () => void;
  onRestore: () => void;
  onAddNode?: (nodeType: string) => void;
  onAddFlow?: (template: { nodes: Array<{ id: string; nodeType: string; initialData?: Record<string, unknown> }>; edges: Array<{ source: string; target: string }> }) => void;
  onUpdateNode?: (nodeId: string, prompt: string) => void;
  canvasState?: () => { nodes: any[]; edges: any[] };
}

// ─── Constants ───────────────────────────────────────────────────────────────

const IRIS_CHAT_STORAGE_KEY = 'iris_chat_history';
const IRIS_POSITION_KEY = 'iris_panel_position';
const IRIS_SETTINGS_KEY = 'iris_settings';
/**
 * Bump when PLATFORM_SYSTEM_PROMPT changes materially. Stored settings with an
 * older (or missing) version get their systemPrompt reset to the new default
 * so all users pick up new capabilities (custom edits are replaced).
 */
const IRIS_PROMPT_VERSION = 2;
const KNODS_ACTION_RE = /\[KNODS_ACTION\](.*?)\[\/KNODS_ACTION\]/gs;

interface CanvasNodeLite {
  id: string;
  type?: string;
  data?: Record<string, any>;
  selected?: boolean;
}

/** First selected node on the canvas (if any) */
function getSelectedNode(state: { nodes: CanvasNodeLite[] } | undefined | null): CanvasNodeLite | null {
  if (!state?.nodes) return null;
  return state.nodes.find(n => n.selected) || null;
}

/** Map a canvas node to the improve-prompt content type via the registry */
function getNodeContentType(node: CanvasNodeLite): 'image' | 'video' | 'text' {
  const reg = (node.type && (getNodeRegistration(node.type) || getNodeByReactFlowType(node.type))) || undefined;
  if (reg?.category === 'image') return 'image';
  if (reg?.category === 'video') return 'video';
  return 'text';
}

/** Build a compact canvas summary to inject as context for Iris */
function buildCanvasSummary(state: { nodes: any[]; edges: any[] }): string {
  if (!state.nodes || state.nodes.length === 0) {
    return '--- CURRENT CANVAS ---\nThe canvas is currently empty.\n--- END CANVAS ---';
  }

  const lines: string[] = ['--- CURRENT CANVAS ---'];
  for (const node of state.nodes) {
    const def = node.data?.definition;
    const title = def?.title || node.type || 'Unknown';
    const prompt = (node.data?.prompt || node.data?.content || '').trim();
    const model = node.data?.selectedModel || '';
    const params = (node.data?.parameters as Record<string, any>) || {};

    let desc = `- ID: ${node.id} | Type: ${title}`;
    if (node.selected) desc += ' | SELECTED';
    if (prompt) desc += ` | Prompt: "${prompt.length > 100 ? prompt.substring(0, 100) + '...' : prompt}"`;
    if (model) desc += ` | Model: ${model}`;
    if (params.duration) desc += ` | Duration: ${params.duration}s`;
    if (params.aspectRatio) desc += ` | Aspect: ${params.aspectRatio}`;
    if (params.resolution) desc += ` | Resolution: ${params.resolution}`;
    lines.push(desc);
  }

  if (state.edges && state.edges.length > 0) {
    lines.push('Connections:');
    for (const edge of state.edges) {
      lines.push(`  ${edge.source} → ${edge.target}`);
    }
  }

  lines.push('--- END CANVAS ---');
  return lines.join('\n');
}

const getStorageKey = (source: string) =>
  source === 'iris' ? IRIS_CHAT_STORAGE_KEY : `agent_chat_${source}`;

const DEFAULT_WIDTH = 380;
const DEFAULT_HEIGHT = 520;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const IrisIcon = ({ size = 24 }: { size?: number }) => (
  <img
    src="/icons/iris-butterfly.svg"
    alt="Iris"
    style={{ width: size, height: size, display: 'block', flexShrink: 0, filter: 'brightness(0) saturate(100%) invert(58%) sepia(68%) saturate(600%) hue-rotate(155deg) brightness(95%)' }}
  />
);

/** Bot icon collection — 24 SVG icons (3 rows of 8) matching Iris style */
const BOT_ICONS: Record<string, { label: string; svg: React.ReactNode }> = {
  // Row 1: Classic bots & sea creatures
  robot: { label: 'Robot', svg: <SmartToyIcon sx={{ fontSize: 'inherit', color: 'inherit' }} /> },
  lobster: { label: 'Lobster', svg: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
      <path d="M19 7c0-1.1-.5-2-1.3-2.7C16.9 3.5 15.5 3 14 3h-4C8.5 3 7.1 3.5 6.3 4.3 5.5 5 5 5.9 5 7c0 .8.3 1.5.7 2.1L3 12l2.5 1.5L4 16l3-1v3h2v-3l1 .5.5 4.5h3l.5-4.5 1-.5v3h2v-3l3 1-1.5-2.5L21 12l-2.7-2.9c.4-.6.7-1.3.7-2.1zm-7 3c-1.7 0-3-1.3-3-3s1.3-3 3-3 3 1.3 3 3-1.3 3-3 3zm-1.5-3a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5zm3 0a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5z"/>
    </svg>
  )},
  crab: { label: 'Crab', svg: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
      <path d="M12 10c-2.2 0-4 1.3-4 3 0 1.7 1.8 3 4 3s4-1.3 4-3c0-1.7-1.8-3-4-3zm-1.5 3.5a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5zm3 0a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5zM3.5 9L5 6.5 7 8l1-1.5C6.3 5.3 5.3 4.5 4 4L2 7l1.5 2zM20 4c-1.3.5-2.3 1.3-4 2.5L17 8l2-1.5L20.5 9 22 7l-2-3zM6 14c-1 0-2-.5-2.5-1L2 14.5l1 2.5 2-1c.3.6.8 1 1.5 1.3L6 14zm12 0l-.5 2.8c.7-.3 1.2-.7 1.5-1.3l2 1 1-2.5-1.5-1.5c-.5.5-1.5 1-2.5 1zm-7 4h2v3h-2z"/>
    </svg>
  )},
  octopus: { label: 'Octopus', svg: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
      <path d="M12 3C8.7 3 6 5.7 6 9c0 1.5.5 2.8 1.4 3.9-.3 1.2-.9 2.3-1.4 3.1.5.5 1 .5 1.5.2.3-.2.7-.8 1-1.5.4.5 1 1.2 1 2.3 0 1 .5 1.5 1 1.5s.8-.3.8-1c0-1.2-.3-2-.5-2.5h.4c0 1.5.2 3 .8 4 .3.5.7.5 1 0 .6-1 .8-2.5.8-4h.4c-.2.5-.5 1.3-.5 2.5 0 .7.3 1 .8 1s1-.5 1-1.5c0-1.1.6-1.8 1-2.3.3.7.7 1.3 1 1.5.5.3 1 .3 1.5-.2-.5-.8-1.1-1.9-1.4-3.1C17.5 11.8 18 10.5 18 9c0-3.3-2.7-6-6-6zm-2 5.5a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
    </svg>
  )},
  jellyfish: { label: 'Jellyfish', svg: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
      <path d="M12 2C7.6 2 4 5.6 4 10c0 1.5.4 2.8 1 4h1.5c-.1-.3-.2-.7-.3-1H8c.2 1.5.5 3 .5 4.5 0 .8-.3 1.5-.5 2l1 .5c.3-.6.5-1.4.5-2.5 0-1.3-.3-2.8-.5-4h2c0 2 .3 4 .3 5.5 0 1-.2 1.5-.3 2l1 .5c.2-.6.3-1.4.3-2.5 0-1.8-.3-3.7-.3-5.5h2c-.2 1.2-.5 2.7-.5 4 0 1.1.2 1.9.5 2.5l1-.5c-.2-.5-.5-1.2-.5-2 0-1.5.3-3 .5-4.5h1.8c-.1.3-.2.7-.3 1H19c.6-1.2 1-2.5 1-4 0-4.4-3.6-8-8-8zm-2 6.5a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
    </svg>
  )},
  whale: { label: 'Whale', svg: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
      <path d="M3 11c0-3.9 3.6-7 8-7 1.3 0 2.6.3 3.7.8L17 3l1 2.5c1.8 1.5 3 3.5 3 5.5 0 3.9-3.6 7-8 7H8l-2 2.5L5 18c-1.2-1.5-2-3.5-2-7zm5 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm5.5-1c0 .3.2.5.5.5s.5-.2.5-.5-.2-.5-.5-.5-.5.2-.5.5zM4 13h2l1-1H4v1z"/>
    </svg>
  )},
  fish: { label: 'Fish', svg: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
      <path d="M12 4c-4 0-7.5 3-9 6.5-.3.6-.3 1.3 0 2C4.5 16 8 19 12 19c3 0 5.5-1.5 7.3-3.8L22 12l-2.7-3.2C17.5 5.5 15 4 12 4zm-1 9a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z"/>
    </svg>
  )},
  seahorse: { label: 'Seahorse', svg: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
      <path d="M14 2c-1.5 0-3 1-3.5 2.5L10 6c-.5-.3-1-.5-1.5-.5C7.1 5.5 6 6.6 6 8c0 .5.2 1 .4 1.4C5 10.3 4 11.8 4 13.5c0 2.2 1.5 4 3.5 4.3V20c0 1.1.9 2 2 2h1c1.1 0 2-.9 2-2v-2c1.7-.3 3-1.8 3-3.5 0-.6-.2-1.2-.4-1.7.9-.8 1.4-1.9 1.4-3.3 0-1-.3-2-.9-2.8.6-.8.9-1.7.9-2.7 0-2.2-1.3-4-3.5-4zM12 5a.75.75 0 1 1 0 1.5A.75.75 0 0 1 12 5z"/>
    </svg>
  )},
  // Row 2: Land animals
  cat: { label: 'Cat', svg: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
      <path d="M12 2C9 2 6.6 3.8 5.8 6.3L3 4v5.5C3 14.6 7 18 12 18s9-3.4 9-8.5V4l-2.8 2.3C17.4 3.8 15 2 12 2zm-2.5 9a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5zm5 0a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5zM12 14c-1 0-1.8-.3-2.3-.7l.6-.8c.4.3 1 .5 1.7.5s1.3-.2 1.7-.5l.6.8c-.5.4-1.3.7-2.3.7z"/>
    </svg>
  )},
  fox: { label: 'Fox', svg: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
      <path d="M6 2L2 8l2 2c-1 2-1 4 0 6 1.5 3 4.5 5 8 5s6.5-2 8-5c1-2 1-4 0-6l2-2-4-6-3 3h-6L6 2zm3.5 9a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5zm5 0a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5zM12 15c-.8 0-1.5-.3-2-.6l.5-.8c.4.3 1 .4 1.5.4s1.1-.1 1.5-.4l.5.8c-.5.3-1.2.6-2 .6z"/>
    </svg>
  )},
  owl: { label: 'Owl', svg: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
      <path d="M12 2C7 2 4 6 4 10c0 3 1.5 5.5 4 7v3h2v-2.3c.6.2 1.3.3 2 .3s1.4-.1 2-.3V20h2v-3c2.5-1.5 4-4 4-7 0-4-3-8-8-8zM9 12a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm6 0a2 2 0 1 1 0-4 2 2 0 0 1 0 4zM9 10.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5zm6 0a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5zM12 14l-1-1h2l-1 1z"/>
    </svg>
  )},
  panda: { label: 'Panda', svg: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
      <path d="M12 3C8 3 5 6 5 10c0 3.5 2.5 6.5 6 7v2h2v-2c3.5-.5 6-3.5 6-7 0-4-3-7-7-7zm-3.5 5C7.7 8 7 8.7 7 9.5S7.7 11 8.5 11 10 10.3 10 9.5 9.3 8 8.5 8zm7 0c-.8 0-1.5.7-1.5 1.5s.7 1.5 1.5 1.5 1.5-.7 1.5-1.5S16.3 8 15.5 8zM8.5 9.5a.5.5 0 1 1 1 0 .5.5 0 0 1-1 0zm6 0a.5.5 0 1 1 1 0 .5.5 0 0 1-1 0zM12 14c-.8 0-1.3-.2-1.5-.5h3c-.2.3-.7.5-1.5.5z"/>
    </svg>
  )},
  wolf: { label: 'Wolf', svg: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
      <path d="M5.5 2L3 7l2 1.5C4 10 3.5 12 4 14c.8 3 3.5 5.5 7 6h2c3.5-.5 6.2-3 7-6 .5-2 0-4-1-5.5L21 7l-2.5-5L16 5h-3l-1-2-1 2H8L5.5 2zM9 10a1.25 1.25 0 1 1 0 2.5A1.25 1.25 0 0 1 9 10zm6 0a1.25 1.25 0 1 1 0 2.5A1.25 1.25 0 0 1 15 10zm-3 5l-2 1.5v1L12 19l2-1.5v-1L12 15z"/>
    </svg>
  )},
  bear: { label: 'Bear', svg: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
      <path d="M5.5 4a2.5 2.5 0 0 0-1.4 4.6C4 9 4 9.5 4 10c0 4.4 3.6 8 8 8s8-3.6 8-8c0-.5 0-1-.1-1.4A2.5 2.5 0 0 0 18.5 4a2.5 2.5 0 0 0-2.4 1.8C15.1 5.3 13.6 5 12 5s-3.1.3-4.1.8A2.5 2.5 0 0 0 5.5 4zM9.5 10a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5zm5 0a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5zM12 15c-1 0-1.7-.2-2-.5l.5-.8c.4.2.9.3 1.5.3s1.1-.1 1.5-.3l.5.8c-.3.3-1 .5-2 .5z"/>
    </svg>
  )},
  bunny: { label: 'Bunny', svg: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
      <path d="M8.5 2C7.7 2 7 2.7 7 3.5v5C6 9.8 5 11.3 5 13c0 2.8 2.2 5 5 5h4c2.8 0 5-2.2 5-5 0-1.7-1-3.2-2-4.5v-5C17 2.7 16.3 2 15.5 2S14 2.7 14 3.5V7h-4V3.5C10 2.7 9.3 2 8.5 2zM10 12a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm-2 4c-.7 0-1.2-.2-1.5-.4l.5-.8c.3.1.6.2 1 .2s.7-.1 1-.2l.5.8c-.3.2-.8.4-1.5.4zM10 19v3h1v-2.5c.3.1.6.1 1 .1s.7 0 1-.1V22h1v-3c-.6.2-1.3.3-2 .3s-1.4-.1-2-.3z"/>
    </svg>
  )},
  frog: { label: 'Frog', svg: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
      <path d="M6 5a3 3 0 0 1 6 0h0a3 3 0 0 1 6 0c0 1-.5 2-1.2 2.6C18.1 8.9 19 10.8 19 13c0 3.3-3.1 6-7 6s-7-2.7-7-6c0-2.2.9-4.1 2.2-5.4C6.5 7 6 6 6 5zm3-1a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm6 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zM9 4.5a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5zm6 0a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5zM12 15c-1.5 0-3-.5-3.5-1l.7-.7c.5.3 1.5.7 2.8.7s2.3-.4 2.8-.7l.7.7c-.5.5-2 1-3.5 1z"/>
    </svg>
  )},
  // Row 3: Fantasy & cosmic
  dragon: { label: 'Dragon', svg: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
      <path d="M7 3l1.5 3H12l1.5-3L15 5l2-2v4c2 1 3 3 3 5.5 0 3-2 5.5-5 6.5l1 5h-2l-1-4c-.6.1-1.3.2-2 .2-.7-.1-1.4-.1-2-.2l-1 4H6l1-5c-3-1-5-3.5-5-6.5C2 8.8 3 7 5 6V3l2 2zm2 8a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm6 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm-3 4l2-2H10l2 2z"/>
    </svg>
  )},
  phoenix: { label: 'Phoenix', svg: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
      <path d="M12 2L9 5l-4-1 2 4-3 3h5l-1 4 4-3 4 3-1-4h5l-3-3 2-4-4 1-3-3zm-1.5 8a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm3 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2zM12 14l-1.5 2L8 17l2 1.5-.5 2.5 2.5-1.5 2.5 1.5-.5-2.5L16 17l-2.5-1L12 14z"/>
    </svg>
  )},
  alien: { label: 'Alien', svg: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
      <path d="M12 2C8 2 5 5.5 5 10c0 3 1 5.5 2.5 7.3.8 1 1.8 1.7 3 2.2L10 22h4l-.5-2.5c1.2-.5 2.2-1.2 3-2.2C18 15.5 19 13 19 10c0-4.5-3-8-7-8zM9 11c-1.1 0-2-.7-2-1.5S7.9 8 9 8s2 .7 2 1.5S10.1 11 9 11zm6 0c-1.1 0-2-.7-2-1.5S13.9 8 15 8s2 .7 2 1.5S16.1 11 15 11zm-3 4l-1.5-.5 1.5-1 1.5 1L12 15z"/>
    </svg>
  )},
  skull: { label: 'Skull', svg: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
      <path d="M12 2C7.6 2 4 5.6 4 10c0 2.8 1.5 5.3 3.8 6.7L7 20h3v-2h4v2h3l-.8-3.3C18.5 15.3 20 12.8 20 10c0-4.4-3.6-8-8-8zM9 12a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm6 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm-4 3h2v1h-2v-1z"/>
    </svg>
  )},
  ghost: { label: 'Ghost', svg: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
      <path d="M12 2C7.6 2 4 5.6 4 10v10l2.5-2 2 2 2-2 1.5 1.5 1.5-1.5 2 2 2-2 2.5 2V10c0-4.4-3.6-8-8-8zM9.5 12a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm5 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z"/>
    </svg>
  )},
  wizard: { label: 'Wizard', svg: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
      <path d="M12 1l-2 6H4l1 1c1 .5 2 1.3 2.5 2.5-.5 1.2-1 2.3-1 3.5 0 3.3 2.7 6 6 6h1c3.3 0 6-2.7 6-6 0-1.2-.5-2.3-1-3.5C18 9.3 19 8.5 20 8l1-1h-6l-3-6zm-2 11a1.25 1.25 0 1 1 0 2.5A1.25 1.25 0 0 1 10 12zm4 0a1.25 1.25 0 1 1 0 2.5A1.25 1.25 0 0 1 14 12zm-2 4.5c-.8 0-1.3-.2-1.5-.5h3c-.2.3-.7.5-1.5.5z"/>
    </svg>
  )},
  star: { label: 'Star', svg: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
      <path d="M12 2l2.4 7.2H22l-6 4.8 2.4 7.2L12 16.4l-6.4 4.8L8 14 2 9.2h7.6L12 2zm-1.5 9.5a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm3 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"/>
    </svg>
  )},
  mushroom: { label: 'Mushroom', svg: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
      <path d="M12 2C7 2 3 5.6 3 10c0 1 .3 2 .7 2.8.3.4.7.2.8-.2.3-1 1-1.6 2-1.6s1.5.5 1.8 1.2c.1.3.5.3.6 0 .3-.7 1-1.2 1.8-1.2.9 0 1.6.5 1.9 1.3.1.3.5.3.6 0 .3-.8 1-1.3 1.9-1.3.8 0 1.5.5 1.8 1.2.1.3.5.3.6 0C18 11.5 18.5 11 19.5 11c1 0 1.7.6 2 1.6.1.4.5.6.8.2.4-.8.7-1.8.7-2.8 0-4.4-4-8-11-8zM10 18v4h4v-4c0-1.1-.9-2-2-2s-2 .9-2 2z"/>
    </svg>
  )},
};

const BotIcon = ({ icon = 'robot', size = 24 }: { icon?: string; size?: number }) => {
  const entry = BOT_ICONS[icon] || BOT_ICONS.robot;
  if (icon === 'robot') {
    return <SmartToyIcon sx={{ fontSize: size, color: 'var(--color-accent-primary)' }} />;
  }
  return (
    <span style={{ fontSize: size, color: 'var(--color-accent-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', width: size, height: size }}>
      {entry.svg}
    </span>
  );
};

const stripActionBlocks = (text: string): string =>
  text.replace(KNODS_ACTION_RE, '').trim();

interface KnodsAction {
  action: string;
  nodeType?: string;
  nodeId?: string;
  prompt?: string;
  nodes?: Array<{ id: string; nodeType: string; initialData?: Record<string, unknown> }>;
  edges?: Array<{ source: string; target: string }>;
}

const parseActionBlocks = (text: string): KnodsAction[] => {
  const results: KnodsAction[] = [];
  let match: RegExpExecArray | null;
  const re = /\[KNODS_ACTION\](.*?)\[\/KNODS_ACTION\]/gs;
  while ((match = re.exec(text)) !== null) {
    try {
      results.push(JSON.parse(match[1]));
    } catch { /* ignore malformed */ }
  }
  return results;
};

const getInitialMessages = (): Message[] => {
  try {
    const stored = localStorage.getItem(IRIS_CHAT_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.map((msg: any) => ({
        ...msg,
        displayContent: msg.displayContent ?? stripActionBlocks(msg.content),
        timestamp: new Date(msg.timestamp),
      }));
    }
  } catch { /* fall through */ }

  return [{
    id: 'welcome',
    role: 'assistant',
    content: `I'm Iris, your Knods co-creator. Tell me what you want to build.`,
    displayContent: `I'm Iris, your Knods co-creator. Tell me what you want to build.`,
    timestamp: new Date(),
  }];
};

const getInitialPosition = (): { x: number; y: number } => {
  try {
    const stored = localStorage.getItem(IRIS_POSITION_KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* fall through */ }
  // Default: lower-left, next to the NodePalette sidebar (~64px wide)
  return {
    x: 72,
    y: Math.max(80, (typeof window !== 'undefined' ? window.innerHeight : 900) - DEFAULT_HEIGHT - 80),
  };
};

// ─── IrisFloatingPanel ────────────────────────────────────────────────────────

export const IrisFloatingPanel: React.FC<IrisFloatingPanelProps> = ({
  isOpen,
  isMinimized,
  onClose,
  onMinimize,
  onRestore,
  onAddNode,
  onAddFlow,
  onUpdateNode,
  canvasState,
}) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { tier } = useCredits();
  // IRIS is a Pro/Corporate feature.
  const isProTier = tier === 'pro' || tier === 'corporate';
  const [messages, setMessages] = useState<Message[]>(getInitialMessages);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [position, setPosition] = useState(getInitialPosition);

  // Agent mode state
  const [activeSource, setActiveSource] = useState<string>('iris');
  const [agentConnections, setAgentConnections] = useState<AgentConnection[]>([]);
  const [streamingContent, setStreamingContent] = useState<string>('');

  // Improve/condense prompt actions
  const [actionBusy, setActionBusy] = useState(false);
  const [selectedNodeInfo, setSelectedNodeInfo] = useState<{ id: string; title: string; hasPrompt: boolean } | null>(null);

  // In-panel settings (flip)
  const [showSettings, setShowSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ name: '', agent_name: 'iris', icon: 'robot', webhook_url: '' });
  const [editingConnectionId, setEditingConnectionId] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedToken, setCopiedToken] = useState(false);
  const [savingConnection, setSavingConnection] = useState(false);
  const [settingsView, setSettingsView] = useState<'list' | 'edit' | 'iris-edit'>('list');

  // Iris model + system prompt settings (persisted to localStorage).
  // When the stored promptVersion is older than IRIS_PROMPT_VERSION the
  // systemPrompt is reset to the current default (model choice is kept).
  const [irisSettings, setIrisSettings] = useState<{ model: string; systemPrompt: string; promptVersion?: number }>(() => {
    try {
      const stored = localStorage.getItem(IRIS_SETTINGS_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // resolveIrisModel runs on BOTH branches: a stored model that has since
        // been retired at the provider must not survive a version match either.
        if (parsed?.promptVersion === IRIS_PROMPT_VERSION && parsed?.systemPrompt) {
          return { ...parsed, model: resolveIrisModel(parsed?.model) };
        }
        return {
          model: resolveIrisModel(parsed?.model),
          systemPrompt: PLATFORM_SYSTEM_PROMPT,
          promptVersion: IRIS_PROMPT_VERSION,
        };
      }
    } catch { /* ignore */ }
    return { model: IRIS_DEFAULT_MODEL, systemPrompt: PLATFORM_SYSTEM_PROMPT, promptVersion: IRIS_PROMPT_VERSION };
  });
  const [irisSettingsForm, setIrisSettingsForm] = useState<{ model: string; systemPrompt: string }>({ model: IRIS_DEFAULT_MODEL, systemPrompt: PLATFORM_SYSTEM_PROMPT });

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const pillWasDragged = useRef(false);
  const dragState = useRef<{ dragging: boolean; startX: number; startY: number; origX: number; origY: number }>({
    dragging: false, startX: 0, startY: 0, origX: 0, origY: 0,
  });

  // Fetch agent connections on mount
  useEffect(() => {
    AgentSettingsService.getInstance().listConnections()
      .then(conns => setAgentConnections(conns.filter(c => c.is_active)))
      .catch(() => { /* no agents available */ });
  }, []);

  // Switch chat history when source changes
  useEffect(() => {
    const key = getStorageKey(activeSource);
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        setMessages(parsed.map((msg: any) => ({
          ...msg,
          displayContent: msg.displayContent ?? stripActionBlocks(msg.content),
          timestamp: new Date(msg.timestamp),
        })));
      } else {
        const isAgent = activeSource !== 'iris';
        const agentName = isAgent
          ? agentConnections.find(c => c.id === activeSource)?.name || 'Agent'
          : 'Iris';
        setMessages([{
          id: 'welcome',
          role: 'assistant',
          content: isAgent
            ? `Connected to ${agentName}. Tell me what you want to build.`
            : `I'm Iris, your Knods co-creator. Tell me what you want to build.`,
          displayContent: isAgent
            ? `Connected to ${agentName}. Tell me what you want to build.`
            : `I'm Iris, your Knods co-creator. Tell me what you want to build.`,
          timestamp: new Date(),
        }]);
      }
    } catch {
      setMessages([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSource]);

  // Persist messages
  useEffect(() => {
    const key = getStorageKey(activeSource);
    try {
      localStorage.setItem(
        key,
        JSON.stringify(messages.map(m => ({ ...m, timestamp: m.timestamp.toISOString() })))
      );
    } catch { /* ignore */ }
  }, [messages, activeSource]);

  // Persist position
  useEffect(() => {
    try { localStorage.setItem(IRIS_POSITION_KEY, JSON.stringify(position)); } catch { /* ignore */ }
  }, [position]);

  // Scroll to bottom on new messages — use container.scrollTop instead of scrollIntoView
  // to avoid scrolling the entire page/body when the panel layout breaks
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  // Scroll to bottom instantly when opened or restored
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => {
        const container = messagesContainerRef.current;
        if (container) container.scrollTop = container.scrollHeight;
      }, 50);
    }
  }, [isOpen, isMinimized]);

  // Focus input when opened or restored
  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  // Track the currently selected canvas node (drives the quick-action chips)
  useEffect(() => {
    if (!isOpen || isMinimized || !canvasState) {
      setSelectedNodeInfo(null);
      return;
    }
    const update = () => {
      const sel = getSelectedNode(canvasState());
      if (!sel) {
        setSelectedNodeInfo(prev => (prev === null ? prev : null));
        return;
      }
      const hasPrompt = String(sel.data?.prompt || sel.data?.content || '').trim().length > 0;
      const title = sel.data?.definition?.title || sel.type || 'node';
      setSelectedNodeInfo(prev =>
        prev && prev.id === sel.id && prev.hasPrompt === hasPrompt && prev.title === title
          ? prev
          : { id: sel.id, title, hasPrompt }
      );
    };
    update();
    const timer = setInterval(update, 1500);
    return () => clearInterval(timer);
  }, [isOpen, isMinimized, canvasState]);

  // ── Drag ──────────────────────────────────────────────────────────────────

  const handleHeaderMouseDown = useCallback((e: React.MouseEvent) => {
    if (isMobile) return; // No dragging on mobile (full-screen)
    if ((e.target as HTMLElement).closest('button')) return; // Don't drag on buttons
    e.preventDefault();
    dragState.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      origX: position.x,
      origY: position.y,
    };

    const onMouseMove = (ev: MouseEvent) => {
      if (!dragState.current.dragging) return;
      const dx = ev.clientX - dragState.current.startX;
      const dy = ev.clientY - dragState.current.startY;
      const newX = Math.max(0, Math.min(window.innerWidth - DEFAULT_WIDTH, dragState.current.origX + dx));
      const newY = Math.max(0, Math.min(window.innerHeight - 60, dragState.current.origY + dy));
      setPosition({ x: newX, y: newY });
    };

    const onMouseUp = () => {
      dragState.current.dragging = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [position, isMobile]);

  // ── Submit ─────────────────────────────────────────────────────────────────

  /** Append an assistant message to the conversation */
  const pushAssistant = useCallback((text: string) => {
    setMessages(prev => [...prev, {
      id: `assistant-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      role: 'assistant',
      content: text,
      displayContent: text,
      timestamp: new Date(),
    }]);
  }, []);

  /**
   * Improve or condense the prompt of a canvas node and write it back.
   * Reuses the same pipelines as the node-level magic wand:
   *  - improve  → PromptImprovementService (+ model best practices from the server KB)
   *  - condense → POST /api/openai/condense (SYSTEM_PROMPTS.CONDENSE server-side)
   */
  const runPromptAction = useCallback(async (kind: 'improve' | 'condense', nodeId?: string) => {
    const state = canvasState?.();
    const target: CanvasNodeLite | null =
      (nodeId ? state?.nodes.find((n: CanvasNodeLite) => n.id === nodeId) : getSelectedNode(state)) || null;

    if (!target) {
      pushAssistant(nodeId
        ? `I couldn't find node \`${nodeId}\` on the canvas — click the node you mean and try again.`
        : 'Select a node on the canvas first, then ask me again.');
      return;
    }

    const promptText = String(target.data?.prompt || target.data?.content || '').trim();
    if (!promptText) {
      pushAssistant("That node has no prompt yet — type a rough draft in it first and I'll take it from there.");
      return;
    }
    if (!onUpdateNode) {
      pushAssistant("I can't edit nodes from here right now.");
      return;
    }

    setActionBusy(true);
    try {
      let newPrompt = '';
      if (kind === 'condense') {
        const response = await fetch('/api/openai/condense', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: promptText }),
        });
        if (!response.ok) throw new Error('Condense request failed');
        const data = await response.json();
        newPrompt = String(data.condensed || '').trim();
      } else {
        const selectedModel = target.data?.selectedModel as string | undefined;
        const kbEntry = await irisKnowledgeService.getModelEntry(selectedModel);
        const result = await promptImprovementService.improvePrompt({
          originalPrompt: promptText,
          contentType: getNodeContentType(target),
          selectedModel,
          nodeParameters: target.data?.parameters as Record<string, any> | undefined,
          modelBestPractices: kbEntry?.text ?? undefined,
        });
        if (!result.success) throw new Error(result.error || 'Improve request failed');
        newPrompt = result.improvedPrompt.trim();
      }

      if (!newPrompt) throw new Error('Empty result');
      onUpdateNode(target.id, newPrompt);
      const title = target.data?.definition?.title || target.type || 'node';
      pushAssistant(`${kind === 'improve' ? '✨ Improved' : '✂️ Condensed'} the prompt on **${title}** and applied it to the node:\n\n${newPrompt}`);
    } catch (error) {
      console.warn('[Iris] Prompt action failed:', error);
      pushAssistant(`Sorry — I couldn't ${kind} that prompt. Please try again.`);
    } finally {
      setActionBusy(false);
    }
  }, [canvasState, onUpdateNode, pushAssistant]);

  // Fire parsed action blocks (shared between Iris and agent modes)
  const fireActions = useCallback((rawContent: string) => {
    if (!onAddNode && !onAddFlow && !onUpdateNode) return;
    const actions = parseActionBlocks(rawContent);
    for (const act of actions) {
      if (act.action === 'addNode' && onAddNode) {
        const nodeType = act.nodeType || (act as any).type;
        if (!nodeType || !getNodeRegistration(nodeType)) {
          console.warn(`[Iris] Skipping unknown node type: "${nodeType}"`);
          continue;
        }
        onAddNode(nodeType);
      } else if (act.action === 'addFlow' && act.nodes && onAddFlow) {
        // Normalize: agents may send `type` instead of `nodeType`
        const normalizedNodes = act.nodes
          .map(n => ({
            id: n.id,
            nodeType: n.nodeType || (n as any).type,
            initialData: n.initialData || (n as any).data,
          }))
          .filter(n => {
            if (!getNodeRegistration(n.nodeType)) {
              console.warn(`[Iris] Filtering out unknown node type: "${n.nodeType}"`);
              return false;
            }
            return true;
          });
        // Also filter edges that reference removed nodes
        const validIds = new Set(normalizedNodes.map(n => n.id));
        const validEdges = (act.edges ?? []).filter(
          e => validIds.has(e.source) && validIds.has(e.target)
        );
        if (normalizedNodes.length > 0) {
          onAddFlow({ nodes: normalizedNodes, edges: validEdges });
        }
      } else if (act.action === 'updateNode' && onUpdateNode) {
        const nodeId = act.nodeId;
        const prompt = act.prompt;
        if (nodeId && prompt) {
          onUpdateNode(nodeId, prompt);
        }
      } else if (act.action === 'improvePrompt' || act.action === 'condensePrompt') {
        // Delegated prompt actions — run the built-in pipelines asynchronously
        void runPromptAction(act.action === 'improvePrompt' ? 'improve' : 'condense', act.nodeId);
      }
    }
  }, [onAddNode, onAddFlow, onUpdateNode, runPromptAction]);

  // Submit to Iris (original path)
  const submitToIris = useCallback(async (userContent: string, history: Array<{ role: string; content: string }>) => {
    const { model, systemPrompt: baseSystemPrompt } = irisSettings;

    let dynamicSystemPrompt = baseSystemPrompt;

    const state = canvasState ? canvasState() : undefined;
    const selectedNode = getSelectedNode(state);
    const selectedModel = selectedNode?.data?.selectedModel as string | undefined;

    // Simple RAG: full KB entry for the selected node's model + up to 2 compact
    // keyword-matched entries for the user's message. Best-effort — Iris works
    // without them if the knowledge API is unavailable.
    try {
      const [modelEntry, searchResults] = await Promise.all([
        irisKnowledgeService.getModelEntry(selectedModel),
        irisKnowledgeService.search(userContent, 2),
      ]);
      if (modelEntry) {
        dynamicSystemPrompt += `\n\n--- MODEL BEST PRACTICES (selected node uses ${selectedModel}) ---\n${modelEntry.text}\n--- END MODEL BEST PRACTICES ---`;
      }
      const extraNotes = searchResults.filter(r => r.id !== modelEntry?.id);
      if (extraNotes.length > 0) {
        dynamicSystemPrompt += `\n\n--- RELEVANT MODEL NOTES ---\n${extraNotes.map(r => r.text).join('\n\n')}\n--- END MODEL NOTES ---`;
      }
    } catch { /* knowledge injection is best-effort */ }

    // Inject canvas state into system prompt
    if (state) {
      dynamicSystemPrompt += `\n\n${buildCanvasSummary(state)}`;
    }

    // Route to the backend that can actually run this model. Every prefix Iris
    // offers has an entry in irisModels.ts and the test asserts it — a miss here
    // used to fall through to the OpenAI route, which reserves credits under
    // provider 'openai' and 500s on the registry miss instead of saying so.
    const endpoint = irisEndpointForModel(model) ?? '/api/openai/chat';

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        prompt: userContent,
        systemPrompt: dynamicSystemPrompt,
        model,
        context: { messages: history.slice(0, -1) },
      }),
    });

    if (!response.ok) throw new Error('API error');
    const data = await response.json();
    const rawContent: string = data.content || 'Sorry, I could not generate a response.';

    fireActions(rawContent);

    const display = stripActionBlocks(rawContent).trim() || '✨ Done — placed on canvas.';
    setMessages(prev => [...prev, {
      id: `assistant-${Date.now()}`,
      role: 'assistant',
      content: rawContent,
      displayContent: display,
      timestamp: new Date(),
    }]);
  }, [fireActions, canvasState, irisSettings]);

  // Submit to Agent (streaming SSE path)
  const submitToAgent = useCallback(async (userContent: string, history: Array<{ role: string; content: string }>) => {
    const apiBase = getApiBase();
    const placeholderId = `assistant-${Date.now()}`;

    // Add streaming placeholder message
    setMessages(prev => [...prev, {
      id: placeholderId,
      role: 'assistant',
      content: '',
      displayContent: '',
      timestamp: new Date(),
    }]);
    setStreamingContent('');

    const response = await authFetch(`${apiBase}/api/agent-chat/message`, {
      method: 'POST',
      body: JSON.stringify({
        agentConnectionId: activeSource,
        message: userContent,
        history: history.slice(0, -1),
      }),
    });

    if (!response.ok) {
      const errText = await response.text().catch(() => 'unknown');
      throw new Error(`Agent API error ${response.status}: ${errText}`);
    }
    if (!response.body) throw new Error('No response stream');

    console.log('[AgentSSE] Stream opened, status:', response.status, 'type:', response.headers.get('content-type'));

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let accumulated = '';
    let buffer = '';
    let currentEvent = 'message'; // default SSE event type

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        console.log('[AgentSSE] Stream ended. Accumulated:', accumulated.length, 'chars');
        break;
      }

      const chunk = decoder.decode(value, { stream: true });
      console.log('[AgentSSE] Chunk received:', chunk.length, 'bytes:', JSON.stringify(chunk.slice(0, 200)));
      buffer += chunk;
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        // Skip SSE comments (lines starting with :)
        if (line.startsWith(':')) continue;

        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith('data: ')) {
          try {
            const parsed = JSON.parse(line.slice(6));

            if (currentEvent === 'pairing_required') {
              const pairingMsg = parsed.message || 'Device pairing required. Approve on your OpenClaw client.';
              accumulated = pairingMsg;
              setMessages(prev => prev.map(m =>
                m.id === placeholderId
                  ? { ...m, content: pairingMsg, displayContent: pairingMsg }
                  : m
              ));
              setStreamingContent('');
              return;
            }

            if (currentEvent === 'error') {
              const errMsg = parsed.error || 'Agent error';
              accumulated = `Error: ${errMsg}`;
              setMessages(prev => prev.map(m =>
                m.id === placeholderId
                  ? { ...m, content: accumulated, displayContent: accumulated }
                  : m
              ));
              setStreamingContent('');
              return;
            }

            if (parsed.delta) {
              accumulated += parsed.delta;
              setStreamingContent(accumulated);
              setMessages(prev => prev.map(m =>
                m.id === placeholderId
                  ? { ...m, content: accumulated, displayContent: stripActionBlocks(accumulated) }
                  : m
              ));
            }

            // Handle done event with content (fallback if delta never arrived)
            if (currentEvent === 'done' && parsed.content && !accumulated) {
              accumulated = parsed.content;
              setMessages(prev => prev.map(m =>
                m.id === placeholderId
                  ? { ...m, content: accumulated, displayContent: stripActionBlocks(accumulated) }
                  : m
              ));
            }
          } catch (e) {
            console.warn('[AgentSSE] Failed to parse SSE data:', line, e);
          }
          currentEvent = 'message'; // Reset after processing data
        }
      }
    }

    // Final: fire actions from accumulated content
    console.log('[AgentSSE] Final accumulated content:', JSON.stringify(accumulated));
    console.log('[AgentSSE] Parsed actions:', parseActionBlocks(accumulated));
    fireActions(accumulated);
    setStreamingContent('');

    // Ensure final message state is correct
    const finalContent = accumulated || 'Agent returned an empty response.';
    const finalDisplay = stripActionBlocks(finalContent).trim() || '✨ Done — placed on canvas.';
    setMessages(prev => prev.map(m =>
      m.id === placeholderId
        ? { ...m, content: finalContent, displayContent: finalDisplay }
        : m
    ));
  }, [activeSource, fireActions]);

  const handleSubmit = useCallback(async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: input.trim(),
      displayContent: input.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const conversationHistory = messages.map(m => ({ role: m.role as 'user' | 'assistant', content: m.content }));
      conversationHistory.push({ role: 'user', content: userMessage.content });

      if (activeSource === 'iris') {
        await submitToIris(userMessage.content, conversationHistory);
      } else {
        await submitToAgent(userMessage.content, conversationHistory);
      }
    } catch {
      setMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        displayContent: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date(),
      }]);
    } finally {
      setIsLoading(false);
      setStreamingContent('');
    }
  }, [input, isLoading, messages, activeSource, submitToIris, submitToAgent]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
  };

  const handleClearChat = useCallback(() => {
    const isAgent = activeSource !== 'iris';
    const agentName = isAgent
      ? agentConnections.find(c => c.id === activeSource)?.name || 'Agent'
      : 'Iris';
    const welcome: Message = {
      id: 'welcome',
      role: 'assistant',
      content: isAgent
        ? `Connected to ${agentName}. Tell me what you want to build.`
        : `I'm Iris, your Knods co-creator. Tell me what you want to build.`,
      displayContent: isAgent
        ? `Connected to ${agentName}. Tell me what you want to build.`
        : `I'm Iris, your Knods co-creator. Tell me what you want to build.`,
      timestamp: new Date(),
    };
    setMessages([welcome]);
    try { localStorage.removeItem(getStorageKey(activeSource)); } catch { /* ignore */ }
  }, [activeSource, agentConnections]);

  // ── In-panel settings ────────────────────────────────────────────────────

  const reloadConnections = useCallback(async () => {
    try {
      const conns = await AgentSettingsService.getInstance().listConnections();
      setAgentConnections(conns.filter(c => c.is_active));
    } catch { /* ignore */ }
  }, []);

  const openSettings = useCallback((connectionId?: string) => {
    if (connectionId === 'iris') {
      // Edit Iris settings
      setIrisSettingsForm({ model: irisSettings.model, systemPrompt: irisSettings.systemPrompt });
      setSettingsView('iris-edit');
      setShowSettings(true);
      return;
    }
    if (connectionId) {
      // Edit a specific connection
      const conn = agentConnections.find(c => c.id === connectionId);
      if (conn) {
        setEditingConnectionId(conn.id);
        setSettingsForm({
          name: conn.name,
          agent_name: conn.agent_name || 'iris',
          icon: conn.icon || 'robot',
          webhook_url: conn.webhook_url || '',
        });
        setSettingsView('edit');
        setShowSettings(true);
        return;
      }
    }
    // Default: show agent list
    setSettingsView('list');
    setShowSettings(true);
  }, [agentConnections, irisSettings]);

  const handleSaveIrisSettings = useCallback(() => {
    const next = { ...irisSettingsForm, promptVersion: IRIS_PROMPT_VERSION };
    setIrisSettings(next);
    try { localStorage.setItem(IRIS_SETTINGS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    setSettingsView('list');
  }, [irisSettingsForm]);

  const openAddAgent = useCallback(() => {
    setEditingConnectionId(null);
    setSettingsForm({ name: '', agent_name: 'iris', icon: 'robot', webhook_url: '' });
    setSettingsView('edit');
  }, []);

  const handleSaveConnection = useCallback(async () => {
    if (!settingsForm.name.trim()) return;
    setSavingConnection(true);
    const service = AgentSettingsService.getInstance();
    try {
      let connectionId = editingConnectionId;
      if (editingConnectionId) {
        await service.updateConnection(editingConnectionId, {
          name: settingsForm.name.trim() || undefined,
          agent_name: settingsForm.agent_name.trim() || undefined,
          icon: settingsForm.icon,
          webhook_url: settingsForm.webhook_url.trim() || null,
        });
      } else {
        const created = await service.createConnection({
          name: settingsForm.name.trim() || undefined,
          agent_name: settingsForm.agent_name.trim() || undefined,
          icon: settingsForm.icon,
          webhook_url: settingsForm.webhook_url.trim() || undefined,
        });
        connectionId = created.id;
      }
      await reloadConnections();
      // Return to agent list and auto-select the bot
      setSettingsView('list');
      if (connectionId) setActiveSource(connectionId);
    } catch (err: any) {
      console.error('Failed to save connection:', err);
    } finally {
      setSavingConnection(false);
    }
  }, [editingConnectionId, settingsForm, reloadConnections]);

  const handleDeleteConnection = useCallback(async () => {
    if (!editingConnectionId) return;
    try {
      await AgentSettingsService.getInstance().deleteConnection(editingConnectionId);
      if (activeSource === editingConnectionId) setActiveSource('iris');
      await reloadConnections();
      setSettingsView('list');
    } catch (err: any) {
      console.error('Failed to delete connection:', err);
    }
  }, [editingConnectionId, activeSource, reloadConnections]);

  const getPollingUrl = useCallback((connectionId: string) => {
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    return `${base}/api/agent-gateway/${connectionId}/updates?token=YOUR_TOKEN`;
  }, []);

  const handleCopyToken = useCallback((token: string) => {
    navigator.clipboard.writeText(token);
    setCopiedToken(true);
    setTimeout(() => setCopiedToken(false), 2000);
  }, []);

  const handleRegenerateToken = useCallback(async () => {
    if (!editingConnectionId) return;
    try {
      await AgentSettingsService.getInstance().regenerateToken(editingConnectionId);
      await reloadConnections();
    } catch (err: any) {
      console.error('Failed to regenerate token:', err);
    }
  }, [editingConnectionId, reloadConnections]);

  const handleCopyPollingUrl = useCallback((connectionId: string) => {
    const conn = agentConnections.find(c => c.id === connectionId);
    const token = conn?.gateway_token || 'YOUR_TOKEN';
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    const url = `${base}/api/agent-gateway/${connectionId}/updates?token=${token}`;
    navigator.clipboard.writeText(url);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  }, [agentConnections]);

  // ── Textarea auto-resize ───────────────────────────────────────────────────

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
  };

  // ── Nothing to render ──────────────────────────────────────────────────────

  if (!isOpen) return null;

  // ── Pro gate: IRIS is a Pro/Corporate feature ──────────────────────────────
  // Show a locked upsell panel rather than the chat for non-pro tiers. The
  // minimized pill stays available so the entry point remains discoverable.
  if (!isProTier && !isMinimized) {
    return createPortal(
      <div
        className={`iris-floating-panel iris-floating-panel--locked${isMobile ? ' iris-floating-panel--mobile' : ''}`}
        style={isMobile ? undefined : { left: position.x, top: position.y, width: DEFAULT_WIDTH }}
      >
        <div className="iris-panel-header" onMouseDown={handleHeaderMouseDown}>
          <div className="iris-panel-title">
            <IrisIcon size={22} />
            <span>Iris</span>
            <span className="iris-pro-badge"><LockIcon sx={{ fontSize: 12 }} /> Pro</span>
          </div>
          <div className="iris-panel-controls">
            <button className="iris-ctrl-btn" onClick={onMinimize} title="Minimize">
              <RemoveIcon fontSize="small" />
            </button>
            <button className="iris-ctrl-btn" onClick={onClose} title="Close">
              <CloseIcon fontSize="small" />
            </button>
          </div>
        </div>
        <div className="iris-locked-body">
          <IrisIcon size={48} />
          <h3>Meet Iris, your AI co-creator</h3>
          <p>
            Iris builds flows, places nodes, and drives your canvas through chat.
            Upgrade to Pro to unlock Iris and agent connections.
          </p>
          <button
            className="iris-locked-upgrade-btn"
            onClick={() => { onClose(); navigate('/pricing'); }}
          >
            Upgrade to Pro
          </button>
        </div>
      </div>,
      document.body
    );
  }

  // ── Minimized: butterfly pill ──────────────────────────────────────────────

  if (isMinimized) {
    return createPortal(
      <button
        className={`iris-minimized-pill${isMobile ? ' iris-minimized-pill--mobile' : ''}`}
        style={isMobile ? undefined : { left: position.x, top: position.y }}
        onClick={() => {
          // Don't restore if we just finished a drag
          if (pillWasDragged.current) { pillWasDragged.current = false; return; }
          onRestore();
        }}
        title="Open Iris"
        onMouseDown={isMobile ? undefined : (e => {
          e.preventDefault();
          pillWasDragged.current = false;
          dragState.current = { dragging: true, startX: e.clientX, startY: e.clientY, origX: position.x, origY: position.y };
          const onMM = (ev: MouseEvent) => {
            if (!dragState.current.dragging) return;
            const dx = ev.clientX - dragState.current.startX;
            const dy = ev.clientY - dragState.current.startY;
            // Mark as dragged once we move more than 4px
            if (Math.abs(dx) > 4 || Math.abs(dy) > 4) pillWasDragged.current = true;
            setPosition({
              x: Math.max(0, Math.min(window.innerWidth - 60, dragState.current.origX + dx)),
              y: Math.max(0, Math.min(window.innerHeight - 60, dragState.current.origY + dy)),
            });
          };
          const onMU = () => {
            dragState.current.dragging = false;
            document.removeEventListener('mousemove', onMM);
            document.removeEventListener('mouseup', onMU);
          };
          document.addEventListener('mousemove', onMM);
          document.addEventListener('mouseup', onMU);
        })}
      >
        <IrisIcon size={28} />
      </button>,
      document.body
    );
  }

  // ── Helper: get active agent icon ──────────────────────────────────────────

  const activeAgent = activeSource !== 'iris' ? agentConnections.find(c => c.id === activeSource) : null;
  const activeAgentIcon = activeAgent?.icon || 'robot';

  // ── Full panel ─────────────────────────────────────────────────────────────

  return createPortal(
    <div
      className={`iris-floating-panel${isMobile ? ' iris-floating-panel--mobile' : ''}`}
      style={isMobile ? undefined : { left: position.x, top: position.y, width: DEFAULT_WIDTH }}
    >
      <div className={`iris-flip-container${showSettings ? ' is-flipped' : ''}`}>
        {/* ── FRONT FACE: Chat ─────────────────────────────────────────── */}
        <div className="iris-flip-face iris-flip-front">
          <div className="iris-front-content">
          {/* Header (drag handle) */}
          <div className="iris-panel-header" onMouseDown={handleHeaderMouseDown}>
            <div className="iris-panel-title">
              {activeSource === 'iris'
                ? <IrisIcon size={22} />
                : <BotIcon icon={activeAgentIcon} size={22} />}
              <span>{activeSource === 'iris' ? 'Iris' : (activeAgent?.name || 'Agent')}</span>
            </div>
            <div className="iris-panel-controls">
              <button
                className="iris-ctrl-btn"
                onClick={() => {
                  setSettingsView('list');
                  setShowSettings(true);
                }}
                title="Agents"
              >
                <SettingsIcon fontSize="small" />
              </button>
              <button className="iris-ctrl-btn" onClick={handleClearChat} title="Clear chat">
                <DeleteSweepIcon fontSize="small" />
              </button>
              <button className="iris-ctrl-btn" onClick={onMinimize} title="Minimize">
                <RemoveIcon fontSize="small" />
              </button>
              <button className="iris-ctrl-btn" onClick={onClose} title="Close">
                <CloseIcon fontSize="small" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="iris-panel-messages" ref={messagesContainerRef}>
            {messages.filter(m => m.displayContent).map(message => (
              <div key={message.id} className={`iris-message ${message.role}`}>
                <div className="iris-message-avatar">
                  {message.role === 'assistant'
                    ? (activeSource === 'iris'
                        ? <IrisIcon size={18} />
                        : <BotIcon icon={activeAgentIcon} size={18} />)
                    : <PersonIcon style={{ fontSize: 18 }} />}
                </div>
                <div className="iris-message-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {message.displayContent}
                  </ReactMarkdown>
                </div>
              </div>
            ))}
            {(isLoading || actionBusy) && !streamingContent && (
              <div className="iris-message assistant">
                <div className="iris-message-avatar">
                  {activeSource === 'iris'
                    ? <IrisIcon size={18} />
                    : <BotIcon icon={activeAgentIcon} size={18} />}
                </div>
                <div className="iris-message-content loading">
                  <span className="typing-indicator"><span /><span /><span /></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick actions for the selected node */}
          {activeSource === 'iris' && selectedNodeInfo?.hasPrompt && (
            <div className="iris-quick-actions">
              <span className="iris-quick-actions__label" title={selectedNodeInfo.title}>{selectedNodeInfo.title}</span>
              <button
                className="iris-quick-action-btn"
                onClick={() => runPromptAction('improve', selectedNodeInfo.id)}
                disabled={isLoading || actionBusy}
                title="Improve the selected node's prompt using model-specific best practices"
              >
                <AutoFixHighIcon sx={{ fontSize: 13 }} /> Improve prompt
              </button>
              <button
                className="iris-quick-action-btn"
                onClick={() => runPromptAction('condense', selectedNodeInfo.id)}
                disabled={isLoading || actionBusy}
                title="Condense the selected node's prompt"
              >
                <CompressIcon sx={{ fontSize: 13 }} /> Condense
              </button>
            </div>
          )}

          {/* Input */}
          <div className="iris-panel-input-area">
            <textarea
              ref={inputRef}
              className="iris-panel-input"
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={activeSource === 'iris' ? 'Ask Iris anything...' : 'Message your agent...'}
              rows={1}
              disabled={isLoading || actionBusy}
            />
            <button
              className="iris-panel-send"
              onClick={handleSubmit}
              disabled={!input.trim() || isLoading || actionBusy}
              title="Send (Enter)"
            >
              <SendIcon fontSize="small" />
            </button>
          </div>

          <div className="iris-panel-footer">
            <kbd>Enter</kbd> send &nbsp;·&nbsp; <kbd>Shift+Enter</kbd> new line
          </div>
          </div>{/* end iris-front-content */}
        </div>

        {/* ── BACK FACE: Settings ──────────────────────────────────────── */}
        <div className="iris-flip-face iris-flip-back">
          <div className="iris-panel-header" onMouseDown={handleHeaderMouseDown}>
            <div className="iris-panel-title">
              <button
                className="iris-ctrl-btn"
                onClick={() => (settingsView === 'edit' || settingsView === 'iris-edit') ? setSettingsView('list') : setShowSettings(false)}
                title={(settingsView === 'edit' || settingsView === 'iris-edit') ? 'Back to list' : 'Back to chat'}
              >
                <ArrowBackIcon fontSize="small" />
              </button>
              <span>{settingsView === 'list' ? 'Agents' : settingsView === 'iris-edit' ? 'Iris Settings' : (editingConnectionId ? 'Edit Agent' : 'Add Agent')}</span>
            </div>
            <div className="iris-panel-controls">
              <button className="iris-ctrl-btn" onClick={onMinimize} title="Minimize">
                <RemoveIcon fontSize="small" />
              </button>
              <button className="iris-ctrl-btn" onClick={onClose} title="Close">
                <CloseIcon fontSize="small" />
              </button>
            </div>
          </div>

          {settingsView === 'list' ? (
            /* ── Agent List View ── */
            <div className="iris-agent-list">
              {/* Iris — always first */}
              <button
                className={`iris-agent-item${activeSource === 'iris' ? ' is-active' : ''}`}
                onClick={() => setActiveSource('iris')}
              >
                <div className="iris-agent-item__icon">
                  <IrisIcon size={24} />
                </div>
                <span className="iris-agent-item__name">Iris</span>
                <button
                  className="iris-agent-item__edit"
                  onClick={(e) => { e.stopPropagation(); openSettings('iris'); }}
                  title="Edit Iris settings"
                >
                  <EditIcon sx={{ fontSize: 16 }} />
                </button>
                {activeSource === 'iris'
                  ? <RadioButtonCheckedIcon sx={{ fontSize: 18, color: 'var(--color-accent-primary, #3b82f6)' }} />
                  : <RadioButtonUncheckedIcon sx={{ fontSize: 18, opacity: 0.4 }} />}
              </button>

              {/* Custom agents */}
              {agentConnections.map(conn => (
                <button
                  key={conn.id}
                  className={`iris-agent-item${activeSource === conn.id ? ' is-active' : ''}`}
                  onClick={() => setActiveSource(conn.id)}
                >
                  <div className="iris-agent-item__icon">
                    <BotIcon icon={conn.icon || 'robot'} size={24} />
                  </div>
                  <span className="iris-agent-item__name">{conn.name}</span>
                  <button
                    className="iris-agent-item__edit"
                    onClick={(e) => {
                      e.stopPropagation();
                      openSettings(conn.id);
                    }}
                    title="Edit agent"
                  >
                    <EditIcon sx={{ fontSize: 16 }} />
                  </button>
                  {activeSource === conn.id
                    ? <RadioButtonCheckedIcon sx={{ fontSize: 18, color: 'var(--color-accent-primary, #3b82f6)' }} />
                    : <RadioButtonUncheckedIcon sx={{ fontSize: 18, opacity: 0.4 }} />}
                </button>
              ))}

              {/* Add Agent button */}
              <button className="iris-agent-add-btn" onClick={openAddAgent}>
                <AddIcon sx={{ fontSize: 18 }} />
                <span>Add Agent</span>
              </button>
            </div>
          ) : settingsView === 'iris-edit' ? (
            /* ── Iris Settings Form ── */
            <div className="iris-settings-body">
              <div className="iris-settings-field">
                <label>Model</label>
                <span className="iris-field-hint">The AI model Iris uses to respond</span>
                <select
                  value={irisSettingsForm.model}
                  onChange={e => setIrisSettingsForm(prev => ({ ...prev, model: e.target.value }))}
                >
                  {/* Curated, not derived from MODEL_REGISTRY — but the list,
                      the routing and the pricing now live together in
                      ./irisModels.ts so a test can hold them in agreement. */}
                  {IRIS_MODEL_GROUPS.map(group => (
                    <optgroup key={group.label} label={group.label}>
                      {group.options.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              <div className="iris-settings-field">
                <label>System Prompt</label>
                <span className="iris-field-hint">Instructions that define Iris's personality and knowledge</span>
                <textarea
                  rows={10}
                  value={irisSettingsForm.systemPrompt}
                  onChange={e => setIrisSettingsForm(prev => ({ ...prev, systemPrompt: e.target.value }))}
                />
              </div>

              <div className="iris-settings-actions">
                <button className="iris-settings-btn secondary iris-reset-prompt-btn" onClick={() => setIrisSettingsForm(prev => ({ ...prev, systemPrompt: PLATFORM_SYSTEM_PROMPT }))}>
                  Reset Prompt
                </button>
                <button className="iris-settings-btn primary" onClick={handleSaveIrisSettings}>
                  Save
                </button>
                <button className="iris-settings-btn secondary" onClick={() => setSettingsView('list')}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            /* ── Edit / Add Agent Form ── */
            <div className="iris-settings-body">
              {/* Icon picker */}
              <div className="iris-settings-field">
                <label>Icon</label>
                <div className="iris-icon-picker">
                  {Object.entries(BOT_ICONS).map(([key, { label }]) => (
                    <button
                      key={key}
                      className={`iris-icon-option${settingsForm.icon === key ? ' selected' : ''}`}
                      onClick={() => setSettingsForm(prev => ({ ...prev, icon: key }))}
                      title={label}
                    >
                      <BotIcon icon={key} size={22} />
                    </button>
                  ))}
                </div>
              </div>

              <div className="iris-settings-field">
                <label>Display Name</label>
                <span className="iris-field-hint">A friendly name shown in the chat header</span>
                <input
                  type="text"
                  placeholder="e.g. Iris Claw, My Code Bot"
                  value={settingsForm.name}
                  onChange={e => setSettingsForm(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>

              <div className="iris-settings-field">
                <label>Agent Name</label>
                <span className="iris-field-hint">The OpenClaw agent identifier to route messages to</span>
                <input
                  type="text"
                  placeholder="e.g. iris, coder, researcher"
                  value={settingsForm.agent_name}
                  onChange={e => setSettingsForm(prev => ({ ...prev, agent_name: e.target.value }))}
                />
              </div>

              <div className="iris-settings-field">
                <label>Webhook URL (optional)</label>
                <span className="iris-field-hint">Messages are pushed here (HMAC-signed). Leave empty to use polling.</span>
                <input
                  type="text"
                  placeholder="https://my-agent.example.com/knods-webhook"
                  value={settingsForm.webhook_url}
                  onChange={e => setSettingsForm(prev => ({ ...prev, webhook_url: e.target.value }))}
                />
              </div>

              {/* Gateway Token & Polling URL (only for existing connections) */}
              {editingConnectionId && (() => {
                const conn = agentConnections.find(c => c.id === editingConnectionId);
                const token = conn?.gateway_token || '';
                return (
                  <>
                    <div className="iris-settings-field">
                      <label>Gateway Token</label>
                      <div className="iris-polling-url">
                        <code>{token.slice(0, 12)}{'...' + token.slice(-6)}</code>
                        <button
                          className="iris-ctrl-btn"
                          onClick={() => handleCopyToken(token)}
                          title="Copy token"
                        >
                          {copiedToken
                            ? <CheckCircleIcon sx={{ fontSize: 14, color: 'var(--color-accent-success, #22c55e)' }} />
                            : <ContentCopyIcon sx={{ fontSize: 14 }} />}
                        </button>
                        <button
                          className="iris-ctrl-btn"
                          onClick={handleRegenerateToken}
                          title="Regenerate token"
                        >
                          <RefreshIcon sx={{ fontSize: 14 }} />
                        </button>
                      </div>
                    </div>
                    {conn?.webhook_url && conn?.webhook_secret && (
                      <div className="iris-settings-field">
                        <label>Webhook Secret</label>
                        <span className="iris-field-hint">Verify X-Knods-Signature (HMAC-SHA256) with this secret</span>
                        <div className="iris-polling-url">
                          <code>{conn.webhook_secret.slice(0, 12)}{'...' + conn.webhook_secret.slice(-6)}</code>
                          <button
                            className="iris-ctrl-btn"
                            onClick={() => handleCopyToken(conn.webhook_secret as string)}
                            title="Copy webhook secret"
                          >
                            <ContentCopyIcon sx={{ fontSize: 14 }} />
                          </button>
                        </div>
                      </div>
                    )}
                    <div className="iris-settings-field">
                      <label>{conn?.webhook_url ? 'Polling URL (fallback)' : 'Polling URL'}</label>
                      <div className="iris-polling-url">
                        <code>{getPollingUrl(editingConnectionId)}</code>
                        <button
                          className="iris-ctrl-btn"
                          onClick={() => handleCopyPollingUrl(editingConnectionId)}
                          title="Copy polling URL"
                        >
                          {copiedUrl
                            ? <CheckCircleIcon sx={{ fontSize: 14, color: 'var(--color-accent-success, #22c55e)' }} />
                            : <ContentCopyIcon sx={{ fontSize: 14 }} />}
                        </button>
                      </div>
                    </div>
                  </>
                );
              })()}

              {/* Actions */}
              <div className="iris-settings-actions">
                <button className="iris-settings-btn primary" onClick={handleSaveConnection} disabled={savingConnection}>
                  {savingConnection ? <span className="iris-save-spinner" /> : (editingConnectionId ? 'Save' : 'Add Agent')}
                </button>
                {editingConnectionId && (
                  <button className="iris-settings-btn danger" onClick={handleDeleteConnection}>
                    Delete
                  </button>
                )}
                <button className="iris-settings-btn secondary" onClick={() => setSettingsView('list')}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── Backward-compat exports ──────────────────────────────────────────────────

/** @deprecated Use IrisFloatingPanel */
export const HelperChat = IrisFloatingPanel;

/** @deprecated No longer used — conversation is now in IrisFloatingPanel */
export const EmbeddedHelperChat: React.FC = () => null;

/** Nav-bar helper button */
export const HelperChatButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button className="helper-chat-button" onClick={onClick} title="Help (Shift+Space)">
    <HelpOutlineIcon />
  </button>
);

export default IrisFloatingPanel;
